"use client";

import { useState, type FormEvent } from "react";
import { useRouter } from "next/navigation";
import { LockKeyhole } from "lucide-react";
import { signIn, signUp, type AuthResult } from "@/app/actions/auth";
import { Brand } from "@/app/components/brand";
import { createUnlockAudio, TrophyOverlay } from "@/app/login/vault-overlay";

export function LoginForm({ configured }: { configured: boolean }) {
  const router = useRouter();
  const [mode, setMode] = useState<"login" | "register">("login");
  const [opening, setOpening] = useState(false);
  const [pending, setPending] = useState(false);
  const [state, setState] = useState<AuthResult>({});

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const audio = createUnlockAudio();
    const formData = new FormData(event.currentTarget);
    setPending(true);
    setState({});

    const result =
      mode === "login" ? await signIn({}, formData) : await signUp({}, formData);
    setPending(false);
    setState(result);

    if (result.error || result.needsConfirmation) {
      audio.cancel();
      return;
    }

    audio.play();
    setOpening(true);
    window.setTimeout(() => {
      router.push("/dashboard");
      router.refresh();
    }, 2800);
  }

  return (
    <main className="flex min-h-screen flex-col items-center px-8 py-8">
      {opening && <TrophyOverlay />}
      <header className="flex w-full max-w-5xl items-center gap-4">
        <Brand />
      </header>
      <div className="flex w-full flex-1 items-center justify-center py-10">
      <div className="relative w-full max-w-md">
        <div className="aurora-orb absolute -inset-8 -z-10 rounded-[2rem] blur-3xl" />
        <div className="neon-frame rounded-3xl bg-zinc-950/80 p-8 shadow-2xl shadow-black/40">
          <div className="aurora-chip inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold">
            <LockKeyhole className="neon-icon h-3.5 w-3.5" />
            Trophytopia
          </div>
          <h1 className="neon-heading mt-4 text-3xl font-bold tracking-tight text-white">
            {mode === "login" ? "Anmelden" : "Konto anlegen"}
          </h1>
          <p className="mt-2 text-sm text-zinc-400">
            {mode === "login"
              ? "Melde dich an, um deine Bibliothek und Trophäen zu sehen."
              : "Erstelle ein Konto. Danach öffnet sich Trophytopia."}
          </p>

          {!configured && (
            <p className="mt-6 rounded-2xl border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
              Trage <span className="font-medium">SUPABASE_URL</span> und{" "}
              <span className="font-medium">SUPABASE_ANON_KEY</span> deines
              Trophytopia-Projekts in <span className="font-medium">.env.local</span> ein
              und führe die SQL-Migration aus.
            </p>
          )}

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <label className="block text-sm text-zinc-300">
              E-Mail
              <input
                name="email"
                type="email"
                required
                autoComplete="email"
                disabled={!configured || pending || opening}
                className="neon-input mt-1.5 w-full rounded-xl border border-zinc-800 bg-zinc-900 px-3 py-2.5 text-white outline-none transition disabled:opacity-60"
              />
            </label>
            <label className="block text-sm text-zinc-300">
              Passwort
              <input
                name="password"
                type="password"
                required
                minLength={6}
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                disabled={!configured || pending || opening}
                className="neon-input mt-1.5 w-full rounded-xl border border-zinc-800 bg-zinc-900 px-3 py-2.5 text-white outline-none transition disabled:opacity-60"
              />
            </label>

            {state.error && (
              <p className="rounded-xl border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                {state.error}
              </p>
            )}
            {state.needsConfirmation && (
              <p className="neon-note rounded-xl border px-3 py-2 text-sm">
                Konto erstellt. Bestätige die E-Mail, danach kannst du dich anmelden.
              </p>
            )}

            <button
              type="submit"
              disabled={!configured || pending || opening}
              className="aurora-btn w-full rounded-xl px-4 py-3 font-semibold transition disabled:cursor-not-allowed disabled:opacity-60"
            >
              {pending
                ? "Prüfe Zugang…"
                : mode === "login"
                  ? "Anmelden"
                  : "Konto erstellen"}
            </button>
          </form>

          <button
            type="button"
            onClick={() => {
              setMode(mode === "login" ? "register" : "login");
              setState({});
            }}
            className="mt-5 text-sm text-zinc-400 transition hover:text-white"
          >
            {mode === "login"
              ? "Noch kein Konto? Jetzt registrieren"
              : "Schon ein Konto? Zum Login"}
          </button>
        </div>
      </div>
      </div>
    </main>
  );
}
